import React, { FC } from 'react';
import styled from 'styled-components';
import { SkeletonBlock, SkeletonPageHeader, SkeletonPageShell, SkeletonRow, SkeletonStack } from 'src/components/widgets/skeleton';

const OrderSkeletonPage = styled.div`
    padding: 30px;
    background-color: #f6f8f9;
    min-width: 0;
`;

const OrderSkeletonShell = styled(SkeletonPageShell).attrs({ $padding: '0' })``;

const OrderSkeletonHeader = styled(SkeletonPageHeader)``;

const OrderWhiteWrapperSkeleton = styled.div`
    border: 1px solid ${(props) => props.theme.app.borderColor};
    border-radius: 8px;
    background: #ffffff;
    padding: 24px;
    min-width: 0;
`;

const OrderSummaryGridSkeleton = styled.div`
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 16px;
    margin-bottom: 24px;
    min-width: 0;

    @media (max-width: 900px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media (max-width: 600px) {
        grid-template-columns: minmax(0, 1fr);
    }
`;

const OrderSectionSkeleton = styled.div`
    border: 1px solid ${(props) => props.theme.skeleton.backgroundColor};
    border-radius: 8px;
    padding: 18px;
    min-width: 0;
`;

const OrderTableSkeleton = styled.div`
    border: 1px solid ${(props) => props.theme.skeleton.backgroundColor};
    border-radius: 8px;
    overflow: hidden;
    min-width: 0;
`;

const OrderTableRowSkeleton = styled.div`
    display: grid;
    grid-template-columns: 70px minmax(180px, 1fr) 140px 110px;
    gap: 18px;
    align-items: center;
    min-height: 64px;
    padding: 14px 18px;
    border-bottom: 1px solid ${(props) => props.theme.skeleton.backgroundColor};
    min-width: 0;

    &:last-child {
        border-bottom: 0;
    }

    @media (max-width: 760px) {
        grid-template-columns: 56px minmax(0, 1fr);
    }
`;

const OrderOptionalColumn = styled.div`
    @media (max-width: 760px) {
        display: none;
    }
`;

export const OrderPageSkeleton: FC = () => (
    <OrderSkeletonPage aria-hidden='true'>
        <OrderSkeletonShell>
            <OrderSkeletonHeader>
                <SkeletonStack $gap='8px' $flex='1'>
                    <SkeletonBlock $width='min(220px, 100%)' $height='28px' />
                    <SkeletonBlock $width='min(280px, 100%)' $height='13px' />
                </SkeletonStack>
                <SkeletonBlock $width='112px' $height='36px' $radius='6px' />
            </OrderSkeletonHeader>
            <OrderWhiteWrapperSkeleton>
                <OrderSummaryGridSkeleton>
                    {Array.from({ length: 4 }).map((_, index) => (
                        <OrderSectionSkeleton key={index}>
                            <SkeletonStack $gap='8px'>
                                <SkeletonBlock $width='70px' $height='12px' />
                                <SkeletonBlock $width={index % 2 === 0 ? '140px' : '100px'} $height='18px' />
                            </SkeletonStack>
                        </OrderSectionSkeleton>
                    ))}
                </OrderSummaryGridSkeleton>
                <SkeletonStack $gap='18px'>
                    <OrderSectionSkeleton>
                        <SkeletonStack $gap='14px'>
                            <SkeletonBlock $width='160px' $height='20px' />
                            <OrderTableSkeleton>
                                {Array.from({ length: 4 }).map((_, index) => (
                                    <OrderTableRowSkeleton key={index}>
                                        <SkeletonBlock $width='56px' $height='56px' $radius='6px' />
                                        <SkeletonStack $gap='8px'>
                                            <SkeletonBlock $width={index % 2 === 0 ? '72%' : '56%'} $height='15px' />
                                            <SkeletonBlock $width='42%' $height='12px' />
                                        </SkeletonStack>
                                        <OrderOptionalColumn>
                                            <SkeletonBlock $width='94px' $height='13px' />
                                        </OrderOptionalColumn>
                                        <OrderOptionalColumn>
                                            <SkeletonBlock $width='70px' $height='22px' $radius='11px' />
                                        </OrderOptionalColumn>
                                    </OrderTableRowSkeleton>
                                ))}
                            </OrderTableSkeleton>
                        </SkeletonStack>
                    </OrderSectionSkeleton>
                    <OrderSectionSkeleton>
                        <SkeletonStack $gap='14px'>
                            <SkeletonBlock $width='190px' $height='20px' />
                            <SkeletonRow $gap='10px'>
                                <SkeletonBlock $width='128px' $height='36px' $radius='6px' />
                                <SkeletonBlock $width='128px' $height='36px' $radius='6px' />
                                <SkeletonBlock $width='128px' $height='36px' $radius='6px' />
                            </SkeletonRow>
                        </SkeletonStack>
                    </OrderSectionSkeleton>
                </SkeletonStack>
            </OrderWhiteWrapperSkeleton>
        </OrderSkeletonShell>
    </OrderSkeletonPage>
);
